import React, { useState } from 'react';
import AuthenticatedImage from './AuthenticatedImage';

interface QuickAnalyzeInputModalProps {
  shopId: string;
  slideId: string;
  suggestedText: string;
  slideImages: Array<{ id: string }>;
  onAnalyze: (userText: string) => void;
  onClose: () => void;
}

const QuickAnalyzeInputModal: React.FC<QuickAnalyzeInputModalProps> = ({
  shopId,
  slideId,
  suggestedText,
  slideImages,
  onAnalyze,
  onClose
}) => {
  const [userText, setUserText] = useState(suggestedText || '');

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-card)',
          padding: '24px',
          borderRadius: '8px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'var(--text-primary)',
          marginBottom: '8px'
        }}>
          Run Quick Analyze
        </h2>

        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '0.875rem',
          marginBottom: '20px',
          lineHeight: '1.5'
        }}>
          Analyze slide images with AI. Edit the text below to provide additional context if needed.
        </p>

        {/* Image Grid */}
        {slideImages.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '12px'
            }}>
              Slide Images ({slideImages.length})
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: '12px'
            }}>
              {slideImages.map((img, index) => (
                <div
                  key={img.id}
                  style={{
                    position: 'relative',
                    paddingBottom: '100%',
                    overflow: 'hidden',
                    borderRadius: '6px',
                    border: '2px solid var(--border-light)'
                  }}
                >
                  <AuthenticatedImage
                    shopId={shopId}
                    slideId={slideId}
                    imageId={img.id}
                    alt={`Image ${index + 1}`}
                    size="thumb_300"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Text Input */}
        <label
          htmlFor="qa-user-text"
          style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 500,
            fontSize: '0.875rem',
            color: 'var(--text-primary)'
          }}
        >
          Additional Information (Optional)
        </label>

        <textarea
          id="qa-user-text"
          value={userText}
          onChange={(e) => setUserText(e.target.value)}
          placeholder="Describe the item, ask questions, or provide context that might help with analysis... (e.g., 'Found at estate sale, has marking on bottom', 'Is this authentic?', 'What era is this from?')"
          rows={4}
          maxLength={2000}
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid var(--border-light)',
            borderRadius: '6px',
            fontSize: '14px',
            marginBottom: '4px',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            resize: 'vertical',
            lineHeight: '1.5'
          }}
        />
        <div style={{
          fontSize: '12px',
          color: 'var(--text-secondary)',
          marginBottom: '16px',
          textAlign: 'right'
        }}>
          {userText.length}/2000 characters
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'var(--border-light)',
              color: 'var(--text-primary)',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Cancel
          </button>

          <button
            onClick={() => onAnalyze(userText)}
            style={{
              backgroundColor: 'var(--primary-dark)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            🔍 Analyze
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickAnalyzeInputModal;
